import {
  Row,
  Input,
  Typography,
  Form,
  Col,
  DatePicker,
  InputNumber,
  Divider,
  Avatar,
  Button,
  Space,
  Spin,
} from "antd";
import { UserRound } from "lucide-react";
import React, { useEffect, useState } from "react";

import { FONT } from "constants/index";

import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import DrawerApp from "components/drawer-app";
import FormScorePoint from "./load-control-drawer-score-point";
import useBreakpoint from "hooks/useBreakpoint";
import { drawerWidth } from "util/responsive";
import { saveControl } from "services/control/control-service";
import {
  notificationErroInternal,
  notificationSucess,
} from "util/api-utils";
import WellBeingRatingInput from "./wellbeing-rating-input";

dayjs.extend(customParseFormat);

const { Text } = Typography;
const dateFormat = "DD/MM/YYYY";

const WELLBEING_FIELDS = [
  { name: "fatigue", label: "Fadiga" },
  { name: "pain", label: "Dor" },
  { name: "humor", label: "Humor" },
  { name: "stress", label: "Estresse" },
  { name: "sleep", label: "Sono" },
];

const FormNewUserControl = (props) => {
  const { isMobile } = useBreakpoint();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [openResultPoint, setOpenResultPoint] = useState(false);

  const editRow = props.editRow;
  const user = props.user;
  const nowControl = props.nowControl ?? user?.nowControl ?? editRow;
  const imgAvatar = user?.img ?? props.imgAvatar;

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue({
      name: user?.name ?? user?.controlActual?.name,
      date: dayjs(new Date()),
      cmjFirst: nowControl?.cmjFirst,
      cmjSecond: nowControl?.cmjSecond,
      fatigue: nowControl?.fatigue,
      humor: nowControl?.humor,
      pain: nowControl?.pain,
      sleep: nowControl?.sleep,
      stress: nowControl?.stress,
    });
  }, [props.user, props.editRow, props.nowControl]);

  const onCloseResultPoint = () => {
    setOpenResultPoint(false);
  };

  const handleCancel = () => {
    form.resetFields();
    props.onClose?.();
  };

  const handleSave = () => {
    setLoading(true);
    form
      .validateFields()
      .then((values) => {
        values.userId = props.userId ?? user?.id;
        values.id = editRow?.id ?? nowControl?.id;
        values.date = values.date.format(dateFormat);

        if (props.isBoot != true) {
          values.reviewId = props.reviewId ?? user?.lastReview?.id;
        }

        saveControl(values)
          .then(() => {
            setLoading(false);
            notificationSucess();
            props.onSaved?.();
            props.onClose?.();
          })
          .catch(() => {
            setLoading(false);
            notificationErroInternal(
              "Aconteceu um erro inesperado, por favor, tente mais tarde."
            );
          });
      })
      .catch(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <Spin spinning={loading}>
        <Form form={form} layout="vertical">
          <Row justify="end" style={{ marginBottom: "25px" }}>
            <Space>
              <Button style={{ fontFamily: FONT }} onClick={handleCancel}>
                Cancelar
              </Button>
              <Button
                style={{ fontFamily: FONT }}
                onClick={handleSave}
                type="primary"
              >
                Salvar
              </Button>
            </Space>
          </Row>
          <Row gutter={16} style={{ textAlign: "center", marginBottom: "30px" }}>
            <Col span={24}>
              <Avatar
                src={imgAvatar}
                icon={<UserRound size={120} />}
                size={120}
              />
            </Col>
          </Row>
          <Row justify="end">
            <Col>
              <Button
                style={{
                  fontFamily: FONT,
                  background: "#14B8A6",
                  color: "#ffffff",
                }}
                onClick={() => setOpenResultPoint(true)}
              >
                Score Bem Estar
              </Button>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={isMobile ? 24 : 12}>
              <Form.Item
                name="name"
                label={<span style={{ fontFamily: FONT }}>Aluno</span>}
              >
                <Input disabled={true} style={{ fontFamily: FONT }} />
              </Form.Item>
            </Col>

            <Col span={isMobile ? 24 : 12}>
              <Form.Item
                name="date"
                label={<span style={{ fontFamily: FONT }}>Data</span>}
                rules={[
                  {
                    required: true,
                    message: "É necessário informar a data da avaliação",
                  },
                ]}
              >
                <DatePicker
                  style={{ fontFamily: FONT, width: "100%" }}
                  placeholder={["", ""]}
                  format={dateFormat}
                  disabled={true}
                  suffixIcon={null}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider />
          <Row gutter={16}>
            <Col span={24} style={{ textAlign: "center" }}>
              <Text style={{ fontSize: "15px", fontFamily: FONT }}>Saltos</Text>
            </Col>
          </Row>
          <Row gutter={16} style={{ marginTop: "15px" }}>
            <Col span={isMobile ? 24 : 12}>
              <Form.Item
                name="cmjFirst"
                label={<span style={{ fontFamily: FONT }}>CMJ Pré</span>}
              >
                <InputNumber
                  decimalSeparator=","
                  style={{ fontFamily: FONT, width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={isMobile ? 24 : 12}>
              <Form.Item
                name="cmjSecond"
                label={<span style={{ fontFamily: FONT }}>CMJ Pós</span>}
              >
                <InputNumber
                  decimalSeparator=","
                  style={{ fontFamily: FONT, width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Row gutter={16} style={{ marginTop: "15px" }}>
            <Col span={24} style={{ textAlign: "center" }}>
              <Text style={{ fontSize: "15px", fontFamily: FONT }}>
                Bem Estar
              </Text>
            </Col>
          </Row>

          {WELLBEING_FIELDS.map((field) => (
            <Row gutter={16} style={{ marginTop: "15px" }} key={field.name}>
              <Col span={24}>
                <Form.Item
                  name={field.name}
                  label={<span style={{ fontFamily: FONT }}>{field.label}</span>}
                >
                  <WellBeingRatingInput />
                </Form.Item>
              </Col>
            </Row>
          ))}
        </Form>
      </Spin>
      <DrawerApp
        open={openResultPoint}
        onClose={onCloseResultPoint}
        title={"Score Bem Estar"}
        body={<FormScorePoint />}
        width={drawerWidth(isMobile, 50)}
        isOnlyView={true}
      />
    </>
  );
};

export default FormNewUserControl;
